import React, { useState, useEffect } from 'react';
import './InstallPrompt.css';

const DISMISS_SESSION_KEY = 'instagreen_install_banner_dismissed';

export function isIOSDevice() {
  if (typeof window === 'undefined') return false;
  return (
    (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

function InstallPrompt({
  lang,
  isStandalone,
  onInstall,
  showGuide,
  onCloseGuide,
}) {
  const [showBanner, setShowBanner] = useState(false);
  const isIOS = isIOSDevice();

  useEffect(() => {
    if (isStandalone) {
      setShowBanner(false);
      return;
    }

    try {
      // Clear legacy 24h localStorage key so testing on mobile isn't locked out
      window.localStorage.removeItem('instagreen_install_dismissed_until');

      const isDismissed = window.sessionStorage.getItem(DISMISS_SESSION_KEY);
      if (isDismissed) {
        return;
      }
    } catch (e) {
      // Ignore storage error
    }

    const timer = setTimeout(() => {
      setShowBanner(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, [isStandalone]);

  const handleDismissBanner = () => {
    setShowBanner(false);
    try {
      window.sessionStorage.setItem(DISMISS_SESSION_KEY, 'true');
    } catch (e) {
      // Ignore storage error
    }
  };

  const handleBannerAction = () => {
    onInstall();
  };

  const publicUrl = process.env.PUBLIC_URL || '';
  const appIcon = `${publicUrl}/apple-touch-icon.png`;

  return (
    <>
      {/* Floating Install Suggestion Popup Banner */}
      {showBanner && !isStandalone && (
        <aside
          className="installBanner"
          role="region"
          aria-label={lang ? "Suggestion d'installation" : "Install suggestion"}
        >
          <div className="installBanner__content">
            <img
              src={appIcon}
              alt="Instagreen"
              className="installBanner__icon"
              onError={(e) => {
                e.currentTarget.src = `${publicUrl}/logo192.png`;
              }}
            />
            <div className="installBanner__text">
              <span className="installBanner__title">Instagreen</span>
              <span className="installBanner__desc">
                {lang
                  ? "Installer l'application pour un accès rapide"
                  : 'Install app for quick access'}
              </span>
            </div>
          </div>
          <div className="installBanner__actions">
            <button
              type="button"
              className="installBanner__btn"
              onClick={handleBannerAction}
            >
              {lang ? 'Installer' : 'Install'}
            </button>
            <button
              type="button"
              className="installBanner__close"
              aria-label={lang ? 'Fermer' : 'Close'}
              onClick={handleDismissBanner}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </aside>
      )}

      {/* Guide Modal (especially tailored for iOS or browsers without native prompt) */}
      {showGuide && (
        <div
          className="installModal__backdrop"
          onClick={onCloseGuide}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="installModal__card"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="installModal__close"
              onClick={onCloseGuide}
              aria-label={lang ? 'Fermer' : 'Close'}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div className="installModal__header">
              <img
                src={appIcon}
                alt="Instagreen"
                className="installModal__logo"
                onError={(e) => {
                  e.currentTarget.src = `${publicUrl}/logo192.png`;
                }}
              />
              <div className="installModal__headerText">
                <h3 className="installModal__title">
                  {lang ? 'Installer Instagreen' : 'Install Instagreen'}
                </h3>
                <p className="installModal__subtitle">
                  {lang
                    ? "Ajoutez l'application sur votre écran d'accueil"
                    : 'Add the app to your Home Screen'}
                </p>
              </div>
            </div>

            {isIOS ? (
              <div className="installModal__steps">
                <div className="installModal__step">
                  <div className="installModal__stepNum">1</div>
                  <div className="installModal__stepIcon">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                      <polyline points="16 6 12 2 8 6" />
                      <line x1="12" y1="2" x2="12" y2="15" />
                    </svg>
                  </div>
                  <div className="installModal__stepBody">
                    <strong>{lang ? 'Appuyez sur Partager' : 'Tap Share'}</strong>
                    <p>
                      {lang
                        ? "Dans la barre Safari en bas, appuyez sur l'icône Partager."
                        : "In Safari's bottom toolbar, tap the Share button."}
                    </p>
                  </div>
                </div>

                <div className="installModal__step">
                  <div className="installModal__stepNum">2</div>
                  <div className="installModal__stepIcon">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="4" />
                      <line x1="12" y1="8" x2="12" y2="16" />
                      <line x1="8" y1="12" x2="16" y2="12" />
                    </svg>
                  </div>
                  <div className="installModal__stepBody">
                    <strong>
                      {lang
                        ? "Sur l'écran d'accueil"
                        : 'Add to Home Screen'}
                    </strong>
                    <p>
                      {lang
                        ? "Faites défiler la liste vers le bas et appuyez sur 'Sur l'écran d'accueil'."
                        : "Scroll down the list and tap 'Add to Home Screen'."}
                    </p>
                  </div>
                </div>

                <div className="installModal__step">
                  <div className="installModal__stepNum">3</div>
                  <div className="installModal__stepIcon">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <div className="installModal__stepBody">
                    <strong>{lang ? 'Confirmez Ajouter' : 'Confirm Add'}</strong>
                    <p>
                      {lang
                        ? "Vérifiez le nom 'Instagreen' et appuyez sur 'Ajouter' en haut à droite."
                        : "Check the name 'Instagreen' and tap 'Add' in the top right."}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="installModal__steps">
                <div className="installModal__step">
                  <div className="installModal__stepNum">1</div>
                  <div className="installModal__stepBody">
                    <strong>{lang ? 'Menu du navigateur' : 'Browser menu'}</strong>
                    <p>
                      {lang
                        ? 'Ouvrez le menu des options de votre navigateur (trois points en haut ou en bas).'
                        : 'Open your browser menu (the three dots in the corner).'}
                    </p>
                  </div>
                </div>

                <div className="installModal__step">
                  <div className="installModal__stepNum">2</div>
                  <div className="installModal__stepBody">
                    <strong>
                      {lang ? 'Installer ou Ajouter' : 'Install or Add'}
                    </strong>
                    <p>
                      {lang
                        ? "Sélectionnez 'Installer Instagreen' ou 'Ajouter à l'écran d'accueil'."
                        : "Select 'Install Instagreen' or 'Add to Home Screen'."}
                    </p>
                  </div>
                </div>

                <div className="installModal__step">
                  <div className="installModal__stepNum">3</div>
                  <div className="installModal__stepBody">
                    <strong>{lang ? 'Confirmer' : 'Confirm'}</strong>
                    <p>
                      {lang
                        ? "Confirmez pour lancer l'application en plein écran."
                        : 'Confirm to launch the app in standalone fullscreen mode.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <button
              type="button"
              className="installModal__actionBtn"
              onClick={onCloseGuide}
            >
              {lang ? "J'ai compris" : 'Got it'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default InstallPrompt;

